import { useNotifications } from "@ilc-technology/luik";
import { ErrorCode, ErrorDetails, HttpPatchOperationType, LineItem, PatchRequestBody, Quote } from "../../Common/Types";
import { LabelKey } from "../../Common/StoryblokTypes";
import { useDatasources } from "../../contexts/StoryblokContext/StoryblokContext";
import { Divider, IconButton, Text } from "@ilc-technology/luik";
import { LineItemSeeDetails } from "../LineItemSeeDetails/LinteItemSeeDetails";
import { FormattedMoney } from "../../Common/Helpers/MoneyHelper";
import { ItemListNames, trackAddToCartEvent, trackRemoveFromCartEvent } from "../../Common/services/Analytics";
import { useQuoteDetailsContext } from "../../contexts/QuoteDetailsContext";
import { logError } from "../../Common/services/ErrorService";
import { findSegmentIndexContainingLineItem, getOptionalItemsForUpgradeUi } from "../../Common/services/UpgradeService";
import { generateItemFromLineItem } from "../../Common/services/AnalyticsMappings";
import { groupLineItems } from "../../Common/groupLineItems";
import React from "react";

interface UpgradeOptionalItemsProps {
  isSaveInProgress: boolean;
  setIsSaveInProgress: (value: boolean) => void;
  currentSegmentIndex: number;
  isMultiQuote: boolean;
  quote: Quote;
  quoteDraft: Quote;
}

export const UpgradeOptionalItems: React.FC<UpgradeOptionalItemsProps> = ({
  isSaveInProgress,
  setIsSaveInProgress,
  currentSegmentIndex,
  isMultiQuote,
  quote,
  quoteDraft,
}) => {
  const notifications = useNotifications();
  const { labels } = useDatasources();
  const { updateQuote } = useQuoteDetailsContext();

  const quoteDraftOptionalItems = getOptionalItemsForUpgradeUi(currentSegmentIndex, quote, quoteDraft, isMultiQuote);
  const groupedOptionalItems = groupLineItems(quoteDraftOptionalItems);

  const toggleOptionalItem = async (lineItem: LineItem) => {
    setIsSaveInProgress(true);
    const segmentIndexForPatch = findSegmentIndexContainingLineItem(quoteDraft, lineItem.id);
    if (segmentIndexForPatch < 0) {
      logError(
        new ErrorDetails({
          code: ErrorCode.QuoteSaveFailed,
          details: {
            additionalDetails: `Optional line item segment not found for id ${lineItem.id}.`,
          },
        })
      );
      setIsSaveInProgress(false);
      return;
    }
    const body: PatchRequestBody[] = [
      {
        op: HttpPatchOperationType.REPLACE,
        path: `/segments/${segmentIndexForPatch}/lineItems/${lineItem?.id}`,
        value: {
          isOptional: !lineItem.isOptional,
        },
      },
    ];
    const result = await updateQuote(quoteDraft, body);
    if (!result.isSuccessful) {
      logError(result.error);
      notifications.addErrorNotification({
        title: labels[LabelKey.error],
        description: labels[LabelKey.upgradeFailed],
      });
    } else {
      const gaItem = generateItemFromLineItem(lineItem, quote, ItemListNames.OptionalItems);
      if (lineItem.isOptional) {
        trackAddToCartEvent(quote.opportunityUuid, gaItem);
      } else {
        trackRemoveFromCartEvent(quote.opportunityUuid, gaItem);
      }
    }
    setIsSaveInProgress(false);
  };

  return (
    <>
      <div className="a-gap-sm flex flex-col" data-testid="upgrade-optional-items">
        {quoteDraftOptionalItems.length > 0 &&
          Object.entries(groupedOptionalItems).map(([groupKey, group], groupIndex) => {
            return (
              <div key={groupKey} className="flex flex-col items-start gap-2">
                <Text variant="paragraph-body-bold">
                  {labels[groupKey as LabelKey] ?? labels[LabelKey.headerOther]}
                </Text>
                {group.lineItems.map((lineItem, lineItemIndex) => (
                  <div key={lineItem.id} className="flex flex-col items-start self-stretch">
                    <div className="flex items-center self-stretch">
                      <div className="flex flex-1 items-center justify-between">
                        <Text variant="label-md-bold">{lineItem?.description}</Text>
                        <Text className="ml-2 text-nowrap" variant="label-md">
                          {lineItem?.totalPrice.amount !== 0
                            ? FormattedMoney(lineItem?.totalPrice, 2, lineItem.isOptional)
                            : labels[LabelKey.included]}
                        </Text>
                      </div>
                      <div className="flex pl-4">
                        <IconButton
                          data-testid={`optional-item-button-${lineItem.sku}`}
                          isDisabled={isSaveInProgress}
                          backgroundClassName={lineItem.isOptional ? "" : "bg-aya"}
                          iconName={lineItem.isOptional ? "plus" : "check"}
                          size="xs"
                          intent="primary-black"
                          aria-label={labels[LabelKey.addButton]}
                          onPress={() => toggleOptionalItem(lineItem)}
                        />
                      </div>
                    </div>
                    <LineItemSeeDetails lineItem={lineItem} />
                    {lineItemIndex !== group.lineItems.length - 1 && <Divider light className="mt-4 w-full" />}
                  </div>
                ))}
                {groupIndex !== Object.keys(groupedOptionalItems).length - 1 && (
                  <Divider light className="mt-2 w-full" />
                )}
              </div>
            );
          })}
      </div>
    </>
  );
};
